import { BaseNodeCommand, NodeChangeRecord } from '../../../core/commands/base/BaseNodeCommand';
import type { Edge } from '../../../core/models/scene';
import type { Point2D } from '../types/layout.types';
import { isOrgEdge } from '../types/layout.types';
import { isReportsToEdge } from '../services/orgChartReportCounts';
import { useDiagramStore } from '../../../stores/diagramStore';
import { useOrgChartViewStore } from '../../../stores/orgchartViewStore';
import { withAuthoredPlacementCleared } from './authoredPlacement';

export interface IncrementalOrgLayoutCommandParams {
  /** New positions for nodes */
  positions: Map<string, Point2D>;
  /** Optional: Only apply to these node IDs */
  affectedNodeIds?: string[];
  /** Optional: Edges for calculating subtree relationships */
  edges?: Edge[];
  /** Optional: Level spacing for subtree push-down (default: 120) */
  levelSpacing?: number;
  /** Optional: Command label */
  label?: string;
}

export interface SubtreePushDownParams {
  /** Root node ID of the subtree to push down */
  rootNodeId: string;
  /** All edges defining the hierarchy */
  edges: Edge[];
  /** Current positions of all nodes */
  currentPositions: Map<string, Point2D>;
  /** Amount to shift Y (default: levelSpacing) */
  deltaY?: number;
}

/**
 * Calculates which nodes belong to a subtree rooted at a given node.
 * Traverses reporting edges to find all descendants.
 */
export function getSubtreeNodeIds(rootNodeId: string, edges: Edge[]): Set<string> {
  const subtreeIds = new Set<string>([rootNodeId]);
  // Subtree is defined by the primary reporting hierarchy only.
  // Secondary org edges (dotted/matrix) must NOT be treated as parent→child links,
  // otherwise delete/layout operations can incorrectly sweep in unrelated nodes.
  const orgEdges = edges.filter(isOrgEdge).filter(isReportsToEdge);

  // Build child map: parent -> children
  const childrenByParent = new Map<string, string[]>();
  orgEdges.forEach(edge => {
    if (isOrgEdge(edge)) {
      // In reporting edge: from = reporter (child), to = manager (parent)
      const children = childrenByParent.get(edge.to) ?? [];
      children.push(edge.from);
      childrenByParent.set(edge.to, children);
    }
  });

  // BFS to find all descendants
  const queue = [rootNodeId];
  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    const children = childrenByParent.get(nodeId) ?? [];
    children.forEach(childId => {
      if (!subtreeIds.has(childId)) {
        subtreeIds.add(childId);
        queue.push(childId);
      }
    });
  }

  return subtreeIds;
}

/**
 * Calculates positions for pushing a subtree down by a given delta Y.
 */
export function calculateSubtreePushDown(params: SubtreePushDownParams): Map<string, Point2D> {
  const { rootNodeId, edges, currentPositions, deltaY = 120 } = params;

  const subtreeIds = getSubtreeNodeIds(rootNodeId, edges);
  const newPositions = new Map<string, Point2D>();

  subtreeIds.forEach(nodeId => {
    const current = currentPositions.get(nodeId);
    if (current) {
      newPositions.set(nodeId, {
        x: current.x,
        y: current.y + deltaY,
      });
    }
  });

  return newPositions;
}

export class IncrementalOrgLayoutCommand extends BaseNodeCommand {
  private readonly positions: Map<string, Point2D>;
  private readonly affectedIds?: Set<string>;

  constructor(params: IncrementalOrgLayoutCommandParams) {
    super(params.label ?? 'Incremental Layout', 'Apply incremental org chart layout');
    // Auto-layout adjustments are system-driven follow-up effects.
    // Keep undo/redo focused on the user action that triggered layout.
    Object.assign(this, { skipHistory: true });
    this.positions = params.positions;
    this.affectedIds = params.affectedNodeIds ? new Set(params.affectedNodeIds) : undefined;
  }

  protected doExecute(): NodeChangeRecord[] {
    if (this.positions.size === 0) return [];

    const mutationManager = this.getMutationManager();
    const storeNodes = useDiagramStore.getState().nodes;
    const nodeById = new Map(storeNodes.map((node) => [node.id, node]));
    const autoLayoutEnabled = useOrgChartViewStore.getState().autoLayoutEnabled;
    const entries = Array.from(this.positions.entries()).filter(([nodeId]) => {
      if (this.affectedIds && !this.affectedIds.has(nodeId)) return false;
      const attrs = (nodeById.get(nodeId)?.attrs ?? {}) as Record<string, unknown>;
      return autoLayoutEnabled || attrs.authoredPlacement !== true;
    });

    const updates = entries.map(([nodeId, position]) => {
      const attrs = (nodeById.get(nodeId)?.attrs ?? {}) as Record<string, unknown>;
      const shouldClearStamp = autoLayoutEnabled && attrs.authoredPlacement === true;
      return {
        nodeId,
        changes: {
          x: position.x,
          y: position.y,
          ...(shouldClearStamp ? { attrs: withAuthoredPlacementCleared(attrs) } : {}),
        },
      };
    });

    if (updates.length === 0) return [];

    if ('applyBatchNodeChanges' in mutationManager && typeof mutationManager.applyBatchNodeChanges === 'function') {
      return mutationManager.applyBatchNodeChanges(updates) ?? [];
    }

    return updates
      .map(({ nodeId, changes }) => mutationManager.applyNodeChange(nodeId, changes))
      .filter(Boolean);
  }
}
